import { clone, type } from 'ramda';
import { IFormData } from '../Utils/BusinessDataFormUtils';
import Logger from '../../../../../../../Utils/Logger';
import { removeEmptyObjects } from '../../../../../../../Model/avenyModelUtils';
import { ValueType } from '../../../../../../../Utils/Formatters/FormatterTypes';

const getSuffix = (key: string) => {
  return key.substring(key.indexOf('_'));
};

const getAssociation = (key: string) => {
  return key.substring(0, key.indexOf('_'));
};

const containsSuffix = (values: IFormData, suffix: string): boolean => {
  if (values._Associations) {
    return Object.keys(values._Associations).some(key => {
      const currentSuffix = getSuffix(key);
      return currentSuffix === suffix;
    });
  }

  return false;
};

export const businessDataFormValuesEqual = (a: ValueType, b: ValueType) => {
  a = typeof a === 'number' ? a : a || null;
  b = typeof b === 'number' ? b : b || null;

  // tslint:disable-next-line: triple-equals
  return a == b;
};

const hasChanged = (data: IFormData, values: IFormData) =>
  Object.keys(values).some(key => {
    if (key !== '_Associations') {
      if (values[key] && !data) {
        return true;
      }
      return !businessDataFormValuesEqual(data[key], values[key]);
    }

    return false;
  });

const updateValuesHasChanged = (
  dataValues: IFormData[],
  updatedValues: IFormData[]
) => {
  if (type(dataValues) === 'Array' && type(updatedValues) === 'Array') {
    return updatedValues.some(updateValue => {
      const originalDataValue = dataValues.find(
        dataValue => dataValue.id === updateValue.id
      );
      if (originalDataValue) {
        return hasChangedDeep(originalDataValue, updateValue);
      }

      return false;
    });
  } else {
    Logger.error(Error('Both params must be arrays'));
    return false;
  }
};

const valuesHasChanged = (data: IFormData, values: IFormData): boolean => {
  if (type(data) === 'Object' && type(values) === 'Object') {
    return hasChangedDeep(data, values);
  } else if (data == null) {
    return data !== values;
  } else {
    Logger.error(Error('Both params must be Object'));
    return false;
  }
};

const refHasChanged = (
  data: IFormData | null,
  ref: string | null | { id: string }
): boolean => {
  if (
    (type(data) === 'Object' || data == null) &&
    (type(ref) === 'String' || ref == null)
  ) {
    // tslint:disable-next-line: triple-equals
    return data?.id != ref;
  } else if (
    (type(data) === 'Object' || data == null) &&
    (type(ref) === 'Object' || ref == null)
  ) {
    return data?.id !== (ref as { id: string })?.id;
  } else {
    Logger.error(
      Error(
        'First param must be nullable Object, second must be nullable string'
      )
    );
    return false;
  }
};

const hasChangedDeep = (data: IFormData, values: IFormData): boolean => {
  const containsAddedOrDeletedAssociations =
    containsSuffix(values, '_Add_Values') ||
    containsSuffix(values, '_Delete_Values') ||
    containsSuffix(values, '_Add_Refs') ||
    containsSuffix(values, '_Delete_Refs');

  if (containsAddedOrDeletedAssociations) {
    return true;
  }

  let result = hasChanged(data, values);

  if (result) {
    return true;
  }

  const dataAssociations = data?._Associations;
  const valuesAssociations = values?._Associations;

  if (!dataAssociations && valuesAssociations != null) {
    const values = clone(valuesAssociations);
    removeEmptyObjects(values, true);
    return Boolean(values);
  }

  if (dataAssociations != null && valuesAssociations != null) {
    return Object.keys(valuesAssociations).some(key => {
      const associationName = getAssociation(key);
      const updateSuffix = getSuffix(key);

      const dataAssociation =
        dataAssociations[associationName] ??
        dataAssociations[`${associationName}_All`];
      const valueAssociation = valuesAssociations[key];

      if (updateSuffix === '_Update_Values') {
        return updateValuesHasChanged(dataAssociation, valueAssociation);
      } else if (updateSuffix === '_Values') {
        return valuesHasChanged(dataAssociation, valueAssociation);
      } else if (updateSuffix === '_Ref') {
        return refHasChanged(dataAssociation, valueAssociation);
      }

      return false;
    });
  }

  return false;
};

const businessDataFormHasChanged = (
  data: IFormData,
  formValues: IFormData
): boolean => {
  return hasChangedDeep(data, formValues);
};

export default businessDataFormHasChanged;
